import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';

import DateTimeRangeField, { type DateTimeRange } from './DateTimeRangeField';
import type { TransportOrderFormType } from './transportOrderFormModel';
import VehicleAssignment from './VehicleAssignment';

export type TransportOrderHubOption = { id: string; label: string };

export type TransportOrderProps = {
    values: TransportOrderFormType;
    onChange: (values: TransportOrderFormType) => void;
    launchHubOptions: TransportOrderHubOption[];
    landingHubOptions: TransportOrderHubOption[];
    excludeTransportOrderId?: string;
};

const SectionHeading = ({ children }: { children: string }) => (
    <p className='text-size-18 text-medium text-color-darkest margin-bottom-0'>{children}</p>
);

const TransportOrderForm = ({
    values,
    onChange,
    launchHubOptions,
    landingHubOptions,
    excludeTransportOrderId,
}: TransportOrderProps) => {
    const update =
        <K extends keyof TransportOrderFormType>(field: K) =>
        (fieldValue: TransportOrderFormType[K]) =>
            onChange({ ...values, [field]: fieldValue });

    const updateHub = (field: 'launchHubId' | 'landingHubId') => (option: SelectOption | undefined) =>
        update(field)(option?.id ?? '');

    const updateWindow = (field: 'pickupWindow' | 'deliveryWindow') => (window: DateTimeRange) =>
        onChange({ ...values, [field]: window, vin: '' });

    return (
        <div className='display-flex flex-column gap-15'>
            <SectionHeading>Pickup</SectionHeading>
            <div className='form-group margin-bottom-0'>
                <label htmlFor='order-launch-hub'>Starting point</label>
                <Select
                    id='order-launch-hub'
                    placeholder='Select starting point'
                    options={launchHubOptions}
                    value={values.launchHubId ? [values.launchHubId] : []}
                    onChange={updateHub('launchHubId')}
                    useFilter
                    useClear
                />
            </div>
            <div className='form-group margin-bottom-0'>
                <label htmlFor='order-pickup-window'>Pickup day and time</label>
                <DateTimeRangeField
                    id='order-pickup-window'
                    label='Select pickup day and time'
                    placeholder='Select pickup day and time'
                    value={values.pickupWindow}
                    onChange={updateWindow('pickupWindow')}
                />
            </div>

            <SectionHeading>Delivery</SectionHeading>
            <div className='form-group margin-bottom-0'>
                <label htmlFor='order-landing-hub'>Endpoint</label>
                <Select
                    id='order-landing-hub'
                    placeholder='Select endpoint'
                    options={landingHubOptions}
                    value={values.landingHubId ? [values.landingHubId] : []}
                    onChange={updateHub('landingHubId')}
                    useFilter
                    useClear
                />
            </div>
            <div className='form-group margin-bottom-0'>
                <label htmlFor='order-delivery-window'>Delivery day and time</label>
                <DateTimeRangeField
                    id='order-delivery-window'
                    label='Select delivery day and time'
                    placeholder='Select delivery day and time'
                    value={values.deliveryWindow}
                    onChange={updateWindow('deliveryWindow')}
                    notBefore={values.pickupWindow.to || undefined}
                />
            </div>

            <SectionHeading>Load ID</SectionHeading>
            <div className='form-group margin-bottom-0'>
                <ClearableInput
                    id='order-load-id'
                    placeholder='e.g: 124556YXI'
                    value={values.loadId}
                    onChange={update('loadId')}
                />
            </div>

            <SectionHeading>Assign vehicle to order</SectionHeading>
            <VehicleAssignment
                pickupWindow={values.pickupWindow}
                deliveryWindow={values.deliveryWindow}
                selectedVin={values.vin}
                onSelect={update('vin')}
                excludeTransportOrderId={excludeTransportOrderId}
            />
        </div>
    );
};

export default TransportOrderForm;
